import { Injectable } from '@angular/core';
import { EnvironmentService } from '@core/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProductListReviewsCollection, Review, ReviewsInfo } from '@reviews/const';
import { ProductListReviewsService, ProductReviewsApiService, ReviewsActions } from '@reviews/data-access';
import { Observable, identity, of } from 'rxjs';
import {
  catchError,
  distinctUntilKeyChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';

const REVIEWS_TIMEOUT = 500;

@Injectable()
export class ReviewsEffects {
  load$: Observable<ReviewsActions.LoadReviewsSuccess | ReviewsActions.LoadReviewsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewsActions.LOAD_REVIEWS),
      distinctUntilKeyChanged('productCode'),
      mergeMap((action: ReviewsActions.LoadReviews) => {
        const { productCode } = action;

        if (this.isRentalCode(productCode)) {
          return of(new ReviewsActions.LoadReviewsFail(productCode, new Error('Арендуемые товары не имеют отзывов')));
        }

        return this.productReviewsApiService.load(productCode).pipe(
          this.environmentService.isPlatformServer ? timeout(REVIEWS_TIMEOUT) : identity,
          map((response: ReviewsInfo) => {
            return new ReviewsActions.LoadReviewsSuccess(productCode, {
              ...response,
              reviews: response.reviews.filter(
                (review: Review) => review.pros || review.cons || review.stringReviewText,
              ),
            });
          }),
          catchError((e: unknown) => of(new ReviewsActions.LoadReviewsFail(productCode, e))),
        );
      }),
    ),
  );

  loadProductList$: Observable<ReviewsActions.LoadProductListReviewSuccess | ReviewsActions.LoadProductListReviewFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ReviewsActions.LOAD_PRODUCT_LIST_REVIEW),
        map((action: ReviewsActions.LoadProductListReview) => action.productCodes),
        withLatestFrom(this.productListReviewsService.getReviewsCollection(), (productCodes, data) => ({
          data,
          productCodes: productCodes.filter(
            (code) => (!data || !Object.prototype.hasOwnProperty.call(data, code)) && !this.isRentalCode(code),
          ),
        })),
        filter(({ productCodes }) => productCodes.length > 0),
        switchMap(({ productCodes, data }) =>
          this.productReviewsApiService.loadProductList(productCodes).pipe(
            this.environmentService.isPlatformServer ? timeout(REVIEWS_TIMEOUT) : identity,
            map((response) => {
              // преобразуем ответ сервера в коллекцию отзывов
              const combinedData = response.reduce<ProductListReviewsCollection>(
                (acc, item) => {
                  if (item.reviewsCount > 0) {
                    acc[item.productCode] = item;
                  }

                  return acc;
                },
                Object.assign({}, data || {}),
              );

              // добавляем в коллекцию ключи, которые не вернул сервер или вернул без отзывов,
              // чтобы в будущем они повторно не запрашивались
              productCodes.forEach((code) => {
                if (!Object.prototype.hasOwnProperty.call(combinedData, code)) {
                  combinedData[code] = null;
                }
              });

              return new ReviewsActions.LoadProductListReviewSuccess(combinedData);
            }),
            catchError((e: unknown) => of(new ReviewsActions.LoadProductListReviewFail(e))),
          ),
        ),
      ),
    );

  add$: Observable<ReviewsActions.AddReviewSuccess | ReviewsActions.AddReviewFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewsActions.ADD_REVIEW_LOAD),
      exhaustMap((action: ReviewsActions.AddReview) => {
        const { params } = action;

        return this.productReviewsApiService.add(params).pipe(
          map(() => new ReviewsActions.AddReviewSuccess()),
          catchError((e: unknown) => of(new ReviewsActions.AddReviewFail(e))),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productReviewsApiService: ProductReviewsApiService,
    private productListReviewsService: ProductListReviewsService,
    private environmentService: EnvironmentService,
  ) {}

  private isRentalCode(code: string): boolean {
    return code[0] === 'r';
  }
}
